import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  Globe,
  Heart,
  Users,
  Radio,
  GlobeHemisphereWest,
  CheckCircle,
  X,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const landingTexts = {
  de: {
    navLogin: 'Einloggen',
    navSignup: 'Registrieren',
    navLanguage: 'Sprache',
    chooseLanguage: 'Sprache wählen',
    displayLanguageFor: 'Anzeigesprache für WORLDofRADIO',
    tapToSelect: 'Zum Auswählen tippen',

    badge: 'Globale Radio-Entdeckungsplattform',
    heroTitle: 'Entdecke den Klang der Welt.',
    heroSubtitle:
      'Entdecke tausende Radiosender aus allen Ecken der Welt. Finde deinen Sound, verbinde dich mit Communities und erlebe Kulturen durch Musik.',
    getStarted: 'Loslegen',
    exploreStations: 'Sender entdecken',

    whyTitle: 'Warum WORLDofRADIO?',
    whySubtitle: 'Dein Zugang zu globalen Audio-Erlebnissen',

    featureGlobalTitle: 'Globale Entdeckung',
    featureGlobalText:
      'Greife auf Radiosender aus über 100 Ländern zu. Entdecke Genres, Sprachen und Kulturen aus der ganzen Welt.',

    featureCollectionTitle: 'Persönliche Sammlung',
    featureCollectionText:
      'Speichere deine Lieblingssender, erstelle eigene Listen und verliere nie den Überblick über die Sounds, die du liebst.',

    featureCommunityTitle: 'Community-getrieben',
    featureCommunityText:
      'Verbinde dich mit anderen Hörern, teile Empfehlungen und entdecke neue Sender durch unsere wachsende Community.',

    ctaTitle: 'Bereit einzuschalten?',
    ctaSubtitle:
      'Werde Teil von WORLDofRADIO und entdecke jeden Tag neue Sounds.',
    ctaButton: 'WORLDofRADIO beitreten',

    footer: 'WORLDofRADIO. Entdecke den Klang der Welt.',
  },

  en: {
    navLogin: 'Log in',
    navSignup: 'Sign up',
    navLanguage: 'Language',
    chooseLanguage: 'Choose language',
    displayLanguageFor: 'Display language for WORLDofRADIO',
    tapToSelect: 'Tap to select',

    badge: 'Global Radio Discovery Platform',
    heroTitle: 'Discover the sound of the world.',
    heroSubtitle:
      'Explore thousands of radio stations from every corner of the globe. Find your sound, connect with communities, and experience cultures through music.',
    getStarted: 'Get Started',
    exploreStations: 'Explore Stations',

    whyTitle: 'Why WORLDofRADIO?',
    whySubtitle: 'Your gateway to global audio experiences',

    featureGlobalTitle: 'Global Discovery',
    featureGlobalText:
      'Access radio stations from over 100 countries. Explore genres, languages, and cultures from around the world.',

    featureCollectionTitle: 'Personal Collections',
    featureCollectionText:
      'Save your favorite stations, create custom playlists, and never lose track of the sounds you love.',

    featureCommunityTitle: 'Community Driven',
    featureCommunityText:
      'Connect with fellow listeners, share recommendations, and discover new stations through our vibrant community.',

    ctaTitle: 'Ready to tune in?',
    ctaSubtitle:
      'Join thousands of listeners discovering new sounds every day.',
    ctaButton: 'Join WORLDofRADIO',

    footer: 'WORLDofRADIO. Discover the sound of the world.',
  },

  no: {
    navLogin: 'Logg inn',
    navSignup: 'Registrer deg',
    navLanguage: 'Språk',
    chooseLanguage: 'Velg språk',
    displayLanguageFor: 'Visningsspråk for WORLDofRADIO',
    tapToSelect: 'Trykk for å velge',

    badge: 'Global plattform for radiooppdagelse',
    heroTitle: 'Oppdag lyden av verden.',
    heroSubtitle:
      'Utforsk tusenvis av radiostasjoner fra hele verden. Finn din lyd, knytt kontakt med fellesskap og opplev kulturer gjennom musikk.',
    getStarted: 'Kom i gang',
    exploreStations: 'Utforsk stasjoner',

    whyTitle: 'Hvorfor WORLDofRADIO?',
    whySubtitle: 'Din inngang til globale lydopplevelser',

    featureGlobalTitle: 'Global oppdagelse',
    featureGlobalText:
      'Få tilgang til radiostasjoner fra over 100 land. Utforsk sjangre, språk og kulturer fra hele verden.',

    featureCollectionTitle: 'Personlige samlinger',
    featureCollectionText:
      'Lagre favorittstasjonene dine, lag egne lister og mist aldri oversikten over lydene du elsker.',

    featureCommunityTitle: 'Drevet av fellesskapet',
    featureCommunityText:
      'Knytt kontakt med andre lyttere, del anbefalinger og oppdag nye stasjoner gjennom vårt voksende fellesskap.',

    ctaTitle: 'Klar til å lytte?',
    ctaSubtitle:
      'Bli med i WORLDofRADIO og oppdag nye lyder hver dag.',
    ctaButton: 'Bli med i WORLDofRADIO',

    footer: 'WORLDofRADIO. Oppdag lyden av verden.',
  },

  ko: {
    navLogin: '로그인',
    navSignup: '가입하기',
    navLanguage: '언어',
    chooseLanguage: '언어 선택',
    displayLanguageFor: 'WORLDofRADIO 표시 언어',
    tapToSelect: '선택하려면 누르세요',

    badge: '글로벌 라디오 탐색 플랫폼',
    heroTitle: '세상의 소리를 발견하세요.',
    heroSubtitle:
      '전 세계 곳곳의 수많은 라디오 방송국을 탐색하세요. 당신만의 소리를 찾고, 커뮤니티와 연결되며, 음악을 통해 문화를 경험하세요.',
    getStarted: '시작하기',
    exploreStations: '방송국 탐색',

    whyTitle: '왜 WORLDofRADIO인가요?',
    whySubtitle: '글로벌 오디오 경험으로 가는 입구',

    featureGlobalTitle: '글로벌 탐색',
    featureGlobalText:
      '100개 이상의 국가에서 제공되는 라디오 방송국을 이용하세요. 전 세계의 장르, 언어, 문화를 탐색할 수 있습니다.',

    featureCollectionTitle: '개인 컬렉션',
    featureCollectionText:
      '좋아하는 방송국을 저장하고 나만의 목록을 만들며, 사랑하는 사운드를 놓치지 마세요.',

    featureCommunityTitle: '커뮤니티 중심',
    featureCommunityText:
      '다른 청취자들과 연결하고 추천을 공유하며, 커뮤니티를 통해 새로운 방송국을 발견하세요.',

    ctaTitle: '들을 준비가 되셨나요?',
    ctaSubtitle:
      'WORLDofRADIO에 참여하고 매일 새로운 소리를 발견하세요.',
    ctaButton: 'WORLDofRADIO 참여하기',

    footer: 'WORLDofRADIO. 세상의 소리를 발견하세요.',
  },

  fi: {
    navLogin: 'Kirjaudu sisään',
    navSignup: 'Rekisteröidy',
    navLanguage: 'Kieli',
    chooseLanguage: 'Valitse kieli',
    displayLanguageFor: 'WORLDofRADIO:n näyttökieli',
    tapToSelect: 'Valitse napauttamalla',

    badge: 'Globaali radioiden löytöalusta',
    heroTitle: 'Löydä maailman ääni.',
    heroSubtitle:
      'Tutustu tuhansiin radioasemiin kaikkialta maailmasta. Löydä oma soundisi, yhdisty yhteisöihin ja koe kulttuureja musiikin kautta.',
    getStarted: 'Aloita',
    exploreStations: 'Tutustu asemiin',

    whyTitle: 'Miksi WORLDofRADIO?',
    whySubtitle: 'Porttisi maailmanlaajuisiin äänikokemuksiin',

    featureGlobalTitle: 'Globaali löytäminen',
    featureGlobalText:
      'Kuuntele radioasemia yli 100 maasta. Tutustu genreihin, kieliin ja kulttuureihin ympäri maailmaa.',

    featureCollectionTitle: 'Omat kokoelmat',
    featureCollectionText:
      'Tallenna suosikkiasemasi, luo omia listoja ja pidä rakastamasi äänet aina tallessa.',

    featureCommunityTitle: 'Yhteisön voima',
    featureCommunityText:
      'Yhdisty muihin kuuntelijoihin, jaa suosituksia ja löydä uusia asemia kasvavan yhteisömme kautta.',

    ctaTitle: 'Valmis kuuntelemaan?',
    ctaSubtitle:
      'Liity WORLDofRADIO:on ja löydä uusia ääniä joka päivä.',
    ctaButton: 'Liity WORLDofRADIO:on',

    footer: 'WORLDofRADIO. Löydä maailman ääni.',
  },
};

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, setLanguage, languages, t } = useLanguage();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const text = landingTexts[language] || landingTexts.en;

  React.useEffect(() => {
    if (user) {
      navigate('/discover');
    }
  }, [user, navigate]);

  const handleLanguageSelect = (code) => {
    setLanguage(code);
    setIsLanguageOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden">
      <div className="relative min-h-screen flex items-center justify-center">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              'url(https://images.pexels.com/photos/34485071/pexels-photo-34485071.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/60 to-[#050505]"></div>
        </div>

        <nav className="absolute top-0 left-0 right-0 z-20 px-6 lg:px-8 py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Radio
                size={32}
                weight="duotone"
                className="text-cyan-400 flex-shrink-0"
              />
              <span
                className="text-xl sm:text-2xl font-black tracking-tighter truncate"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                WORLDofRADIO
              </span>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <button
                type="button"
                onClick={() => setIsLanguageOpen(true)}
                className="text-gray-300 hover:text-cyan-400 font-medium px-3 sm:px-4 py-2 rounded-full transition-colors flex items-center gap-2"
              >
                <GlobeHemisphereWest size={20} />
                <span className="hidden sm:inline">{text.navLanguage}</span>
              </button>

              <button
                data-testid="landing-login-btn"
                onClick={() => navigate('/login')}
                className="text-gray-300 hover:text-cyan-400 font-medium px-3 sm:px-6 py-2 rounded-full transition-colors"
              >
                {text.navLogin}
              </button>

              <button
                data-testid="landing-signup-btn"
                onClick={() => navigate('/register')}
                className="bg-cyan-500 text-black font-bold rounded-full px-5 sm:px-8 py-3 hover:bg-cyan-400 hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all"
              >
                {text.navSignup}
              </button>
            </div>
          </div>
        </nav>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="mb-6">
            <span className="text-xs tracking-[0.2em] uppercase font-mono text-cyan-400">
              {text.badge}
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {text.heroTitle}
          </h1>

          <p className="text-base sm:text-lg leading-relaxed text-gray-300 mb-12 max-w-2xl mx-auto">
            {text.heroSubtitle}
          </p>

          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              data-testid="hero-get-started-btn"
              onClick={() => navigate('/register')}
              className="bg-cyan-500 text-black font-bold rounded-full px-10 py-4 hover:bg-cyan-400 hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all flex items-center gap-2 text-lg"
            >
              <Play size={24} weight="fill" />
              {text.getStarted}
            </button>

            <button
              onClick={() => navigate('/discover')}
              className="bg-white/5 text-white border border-white/10 rounded-full px-10 py-4 hover:bg-white/10 transition-all text-lg font-semibold"
            >
              {text.exploreStations}
            </button>
          </div>
        </div>

        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-cyan-400 rounded-full animate-bounce"></div>
          </div>
        </div>
      </div>

      <section className="py-24 sm:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {text.whyTitle}
            </h2>

            <p className="text-base leading-relaxed text-gray-300 max-w-2xl mx-auto">
              {text.whySubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#0A0E17] border border-white/5 rounded-2xl p-8 hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,255,255,0.15)] hover:-translate-y-1 transition-all duration-300">
              <Globe
                size={48}
                weight="duotone"
                className="text-cyan-400 mb-4"
              />

              <h3
                className="text-xl sm:text-2xl tracking-tight font-semibold mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {text.featureGlobalTitle}
              </h3>

              <p className="text-sm text-gray-400">
                {text.featureGlobalText}
              </p>
            </div>

            <div className="bg-[#0A0E17] border border-white/5 rounded-2xl p-8 hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,255,255,0.15)] hover:-translate-y-1 transition-all duration-300">
              <Heart
                size={48}
                weight="duotone"
                className="text-cyan-400 mb-4"
              />

              <h3
                className="text-xl sm:text-2xl tracking-tight font-semibold mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {text.featureCollectionTitle}
              </h3>

              <p className="text-sm text-gray-400">
                {text.featureCollectionText}
              </p>
            </div>

            <div className="bg-[#0A0E17] border border-white/5 rounded-2xl p-8 hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,255,255,0.15)] hover:-translate-y-1 transition-all duration-300">
              <Users
                size={48}
                weight="duotone"
                className="text-cyan-400 mb-4"
              />

              <h3
                className="text-xl sm:text-2xl tracking-tight font-semibold mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {text.featureCommunityTitle}
              </h3>

              <p className="text-sm text-gray-400">
                {text.featureCommunityText}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-12">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {text.ctaTitle}
            </h2>

            <p className="text-base leading-relaxed text-gray-300 mb-8">
              {text.ctaSubtitle}
            </p>

            <button
              data-testid="cta-join-btn"
              onClick={() => navigate('/register')}
              className="bg-cyan-500 text-black font-bold rounded-full px-10 py-4 hover:bg-cyan-400 hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all text-lg"
            >
              {text.ctaButton}
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-400">
          <p>&copy; 2026 {text.footer}</p>

          <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => navigate('/impressum')}
              className="text-gray-400 hover:text-cyan-300 transition-colors font-semibold"
            >
              {t.imprintLink}
            </button>

            <span className="text-gray-700">·</span>

            <button
              type="button"
              onClick={() => navigate('/datenschutz')}
              className="text-gray-400 hover:text-cyan-300 transition-colors font-semibold"
            >
              {t.privacyLink}
            </button>
          </div>
        </div>
      </footer>

      {isLanguageOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center px-4">
          <div className="w-full max-w-md max-h-[78vh] rounded-t-3xl md:rounded-3xl border border-white/10 bg-[#070A10] shadow-[0_20px_80px_rgba(0,0,0,0.65)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <GlobeHemisphereWest
                  size={26}
                  weight="duotone"
                  className="text-cyan-400"
                />

                <div>
                  <h2
                    className="text-lg font-black text-white tracking-tight"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {text.chooseLanguage}
                  </h2>

                  <p className="text-xs text-gray-400">
                    {text.displayLanguageFor}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsLanguageOpen(false)}
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[58vh] overflow-y-auto">
              {languages.map((item, index) => {
                const isActive = language === item.code;

                return (
                  <button
                    key={item.code}
                    type="button"
                    onClick={() => handleLanguageSelect(item.code)}
                    className={`w-full flex items-center justify-between px-5 py-4 text-left transition-all ${isActive
                        ? 'bg-cyan-400/10 text-cyan-300'
                        : 'text-gray-300 hover:bg-white/5 hover:text-cyan-300'
                      } ${index !== languages.length - 1
                        ? 'border-b border-white/10'
                        : ''
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-8 rounded-full flex items-center justify-center text-xs font-black border ${isActive
                            ? 'border-cyan-400 bg-cyan-400 text-black'
                            : 'border-white/10 bg-white/5 text-gray-400'
                          }`}
                      >
                        {item.label}
                      </div>

                      <div>
                        <div className="text-sm font-bold">{item.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {text.tapToSelect}
                        </div>
                      </div>
                    </div>

                    {isActive && (
                      <CheckCircle
                        size={22}
                        weight="fill"
                        className="text-cyan-400 flex-shrink-0"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;